'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Package {
  id: string;
  name: string;
  type: 'wireless' | 'optical' | 'tv' | 'surveillance' | 'msat' | 'webdev' | 'broadcasting';
  price: string;
  features: string[];
  popular?: boolean;
}

const PackageShowcase = () => {
  const [selectedType, setSelectedType] = useState<'wireless' | 'optical' | 'tv' | 'surveillance' | 'msat' | 'webdev' | 'broadcasting'>('wireless');

  const packages: Package[] = [
    // Wireless Internet Packages
    {
      id: 'wireless-basic',
      name: 'Wireless Internet - Osnovni',
      type: 'wireless',
      price: '30',
      features: ['30 Mbps download / 6 Mbps upload', 'Neograničen promet', 'Besplatna instalacija', 'Dodajte TV za samo +10 KM'],
    },
    {
      id: 'wireless-business',
      name: 'Wireless Internet - Biznis',
      type: 'wireless',
      price: '35',
      features: ['35 Mbps download / 6 Mbps upload', 'Neograničen promet', 'Prioritetna tehnička podrška', 'Dodajte TV za samo +10 KM'],
      popular: true,
    },
    {
      id: 'wireless-pro',
      name: 'Wireless Internet - Pro',
      type: 'wireless',
      price: '40',
      features: ['50 Mbps download / 6 Mbps upload', 'Neograničen promet', '24/7 tehnička podrška', 'Dodajte TV za samo +10 KM'],
    },
    // Optical Internet Packages
    {
      id: 'optical-basic',
      name: 'Optički Internet - Osnovni',
      type: 'optical',
      price: '20',
      features: ['30 Mbps download / 6 Mbps upload', 'Besplatan WiFi ruter', 'Neograničen promet', 'Dodajte TV za samo +10 KM'],
    },
    {
      id: 'optical-business',
      name: 'Optički Internet - Biznis',
      type: 'optical',
      price: '30',
      features: ['40 Mbps download / 8 Mbps upload', 'Besplatan WiFi ruter', 'Prioritetna podrška', 'Dodajte TV za samo +10 KM'],
      popular: true,
    },
    {
      id: 'optical-pro',
      name: 'Optički Internet - Pro',
      type: 'optical',
      price: '35',
      features: ['50 Mbps download / 20 Mbps upload', 'Besplatan WiFi ruter', '24/7 podrška', 'Dodajte TV za samo +10 KM'],
    },
    {
      id: 'optical-max',
      name: 'Optički Internet - Max',
      type: 'optical',
      price: '50',
      features: ['100 Mbps download / 50 Mbps upload', 'Besplatan WiFi 6 mesh sistem', 'Premium podrška', 'Dodajte TV za samo +10 KM'],
    },
    // TV Packages
    {
      id: 'tv-cable',
      name: 'Kablovska Televizija',
      type: 'tv',
      price: '10',
      features: ['100+ TV kanala', '3 TV prijemnika GRATIS', 'HD kvalitet', 'Uz internet paket'],
    },
    {
      id: 'tv-iptv',
      name: 'IPTV',
      type: 'tv',
      price: '10',
      features: ['150+ TV kanala', 'Prvi TV Box uključen', '+5 KM po dodatnom Boxu', '4K Ultra HD'],
      popular: true,
    },
    // Video Surveillance Services
    {
      id: 'surveillance-basic',
      name: 'Video Nadzor - Osnovni Paket',
      type: 'surveillance',
      price: '999',
      features: ['4 HD sigurnosne kamere', 'DVR/NVR rekorder uključen', 'Kompletna profesionalna instalacija', '12 mjeseci garancije'],
      popular: true,
    },
    {
      id: 'surveillance-custom',
      name: 'Video Nadzor - Prilagođeni Paket',
      type: 'surveillance',
      price: 'Po dogovoru',
      features: ['Prilagođen broj kamera', 'Profesionalni NVR sistem', 'Mobilni pristup', 'Proširena garancija'],
    },
    // M-SAT TV Installation
    {
      id: 'msat-installation',
      name: 'M-SAT TV Instalacija',
      type: 'msat',
      price: 'Po cjenovniku',
      features: ['Ovlaštena M-SAT instalacija za BiH', 'Profesionalno postavljanje antene', 'Kompletna montaža i podešavanje', 'Garancija na instalaciju'],
      popular: true,
    },
    {
      id: 'msat-premium',
      name: 'M-SAT Premium Paket',
      type: 'msat',
      price: 'Po dogovoru',
      features: ['Premium M-SAT kanali', 'HD/4K kvalitet', 'Dodatni prijemnici', 'VIP instalacija'],
    },
    // Website & Application Development Services
    {
      id: 'webdev-basic',
      name: 'Web Stranica - Starter',
      type: 'webdev',
      price: 'Od 350',
      features: ['Responzivni dizajn', 'Do 5 stranica', 'Hosting i domena 1 godina', 'SEO optimizacija'],
      popular: true,
    },
    {
      id: 'webdev-business',
      name: 'Web Aplikacija - Business',
      type: 'webdev',
      price: 'Po dogovoru',
      features: ['Custom dizajn', 'Baza podataka', 'Sigurnost i autentifikacija', 'Održavanje 6 mjeseci'],
    },
    {
      id: 'webdev-mobile',
      name: 'Mobilna Aplikacija',
      type: 'webdev',
      price: 'Po dogovoru',
      features: ['iOS i Android', 'Native ili Flutter', 'Backend infrastruktura', '12 mjeseci održavanja'],
    },
    // Live Broadcasting Services
    {
      id: 'broadcast-sports',
      name: 'Live Stream - Sport',
      type: 'broadcasting',
      price: 'Po dogovoru',
      features: ['Multi-kamera setup', 'HD/4K kvalitet', 'Streaming na više platformi', 'Arhiviranje prenosa'],
      popular: true,
    },
    {
      id: 'broadcast-events',
      name: 'Live Stream - Događaji',
      type: 'broadcasting',
      price: 'Po dogovoru',
      features: ['Profesionalna oprema', 'Audio miksanje', 'Interakcija sa publikom', 'Cloud recording'],
    },
    {
      id: 'broadcast-complete',
      name: 'Live Stream - Kompletno',
      type: 'broadcasting',
      price: 'Po dogovoru',
      features: ['Vlastita platforma', 'Pay-per-view opcija', 'Analitika u realnom vremenu', 'Tehnička podrška 24/7'],
    },
  ];

  const filteredPackages = packages.filter(pkg => pkg.type === selectedType);

  const packageTypes = [
    { id: 'wireless', label: 'Wireless Internet', icon: 'WifiIcon' },
    { id: 'optical', label: 'Optički Internet', icon: 'SignalIcon' },
    { id: 'tv', label: 'Televizija', icon: 'TvIcon' },
    { id: 'surveillance', label: 'Video Nadzor', icon: 'VideoCameraIcon' },
    { id: 'msat', label: 'M-SAT TV', icon: 'SignalIcon' },
    { id: 'webdev', label: 'Web & App Razvoj', icon: 'CodeBracketIcon' },
    { id: 'broadcasting', label: 'Live Stream', icon: 'VideoCameraIcon' },
  ];

  return (
    <section data-component-id="src/app/homepage/components/PackageShowcase.tsx:182:4" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="182" data-component-file="PackageShowcase.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20bg-background%22%7D" className="py-20 bg-background">
      <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:183:6" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="183" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:184:8" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="184" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h2 data-component-id="src/app/homepage/components/PackageShowcase.tsx:185:10" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="185" data-component-file="PackageShowcase.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-bold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Prona%C4%91ite%20savr%C5%A1en%20paket%20za%20va%C5%A1e%20potrebe%22%7D" className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Pronađite savršen paket za vaše potrebe
          </h2>
          <p data-component-id="src/app/homepage/components/PackageShowcase.tsx:188:10" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="188" data-component-file="PackageShowcase.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Transparentne%20cijene%2C%20bez%20skrivenih%20tro%C5%A1kova.%20Svi%20paketi%20uklju%C4%8Duju%20na%C5%A1u%20vrhunsku%20korisni%C4%8Dku%20podr%C5%A1ku.%22%7D" className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Transparentne cijene, bez skrivenih troškova. Svi paketi uključuju našu vrhunsku korisničku podršku.
          </p>
        </div>

        <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:193:8" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="193" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-4%20mb-12%22%7D" className="flex flex-wrap justify-center gap-4 mb-12">
          {packageTypes.map((type) => (
            <button data-component-id="src/app/homepage/components/PackageShowcase.tsx:195:12" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="195" data-component-file="PackageShowcase.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={type.id}
              onClick={() => setSelectedType(type.id as 'wireless' | 'optical' | 'tv' | 'surveillance' | 'msat' | 'webdev' | 'broadcasting')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-smooth ${
                selectedType === type.id
                  ? 'bg-primary text-white shadow-atmospheric'
                  : 'bg-card text-foreground hover:bg-muted border border-border'
              }`}
            >
              <Icon data-component-id="src/app/homepage/components/PackageShowcase.tsx:204:14" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="204" data-component-file="PackageShowcase.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={type.icon} size={20} />
              <span data-component-id="src/app/homepage/components/PackageShowcase.tsx:205:14" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="205" data-component-file="PackageShowcase.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{type.label}</span>
            </button>
          ))}
        </div>

        <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:210:8" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="210" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPackages.map((pkg) => (
            <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:212:12" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="212" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={pkg.id}
              className={`relative bg-card rounded-2xl p-8 border-2 transition-smooth hover:shadow-atmospheric-hover ${
                pkg.popular
                  ? 'border-sunset-orange shadow-atmospheric'
                  : 'border-border hover:border-primary'
              }`}
            >
              {pkg.popular && (
                <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:221:16" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="221" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-top-4%20left-1%2F2%20-translate-x-1%2F2%20px-4%20py-1%20bg-sunset-orange%20text-white%20text-sm%20font-semibold%20rounded-full%22%2C%22textContent%22%3A%22Najpopularniji%22%7D" className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-sunset-orange text-white text-sm font-semibold rounded-full">
                  Najpopularniji
                </div>
              )}

              <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:226:14" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="226" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
                <h3 data-component-id="src/app/homepage/components/PackageShowcase.tsx:227:16" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="227" data-component-file="PackageShowcase.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-foreground%20mb-2%22%7D" className="text-2xl font-bold text-foreground mb-2">{pkg.name}</h3>
                <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:228:16" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="228" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-baseline%22%7D" className="flex items-baseline">
                  <span data-component-id="src/app/homepage/components/PackageShowcase.tsx:229:18" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="229" data-component-file="PackageShowcase.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-4xl%20font-bold%20text-primary%22%7D" className="text-4xl font-bold text-primary">{pkg.price}</span>
                  <span data-component-id="src/app/homepage/components/PackageShowcase.tsx:230:18" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="230" data-component-file="PackageShowcase.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20ml-2%22%2C%22textContent%22%3A%22KM%22%7D" className="text-lg text-muted-foreground ml-2">KM</span>
                </div>
              </div>

              <ul data-component-id="src/app/homepage/components/PackageShowcase.tsx:234:14" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="234" data-component-file="PackageShowcase.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-4%20mb-8%22%7D" className="space-y-4 mb-8">
                {pkg.features.map((feature, index) => (
                  <li data-component-id="src/app/homepage/components/PackageShowcase.tsx:236:18" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="236" data-component-file="PackageShowcase.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20items-start%22%7D" key={index} className="flex items-start">
                    <Icon data-component-id="src/app/homepage/components/PackageShowcase.tsx:237:20" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="237" data-component-file="PackageShowcase.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-success%20mr-3%20mt-0.5%20flex-shrink-0%22%7D" name="CheckCircleIcon" size={20} className="text-success mr-3 mt-0.5 flex-shrink-0" />
                    <span data-component-id="src/app/homepage/components/PackageShowcase.tsx:238:20" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="238" data-component-file="PackageShowcase.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-foreground%22%7D" className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link data-component-id="src/app/homepage/components/PackageShowcase.tsx:243:14" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="243" data-component-file="PackageShowcase.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fservices%22%2C%22textContent%22%3A%22Saznajte%20vi%C5%A1e%22%7D"
                href="/services"
                className={`block w-full px-6 py-3 text-center font-semibold rounded-lg transition-smooth ${
                  pkg.popular
                    ? 'bg-sunset-orange text-white hover:bg-opacity-90' :'bg-primary text-white hover:bg-opacity-90'
                }`}
              >
                Saznajte više
              </Link>
            </div>
          ))}
        </div>

        <div data-component-id="src/app/homepage/components/PackageShowcase.tsx:256:8" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="256" data-component-file="PackageShowcase.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mt-12%22%7D" className="text-center mt-12">
          <Link data-component-id="src/app/homepage/components/PackageShowcase.tsx:257:10" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="257" data-component-file="PackageShowcase.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fservices%22%2C%22className%22%3A%22inline-flex%20items-center%20text-primary%20font-semibold%20hover%3Atext-sky-blue%20transition-smooth%22%2C%22textContent%22%3A%22Pogledajte%20sve%20pakete%20i%20cijene%22%7D"
            href="/services"
            className="inline-flex items-center text-primary font-semibold hover:text-sky-blue transition-smooth"
          >
            Pogledajte sve pakete i cijene
            <Icon data-component-id="src/app/homepage/components/PackageShowcase.tsx:262:12" data-component-path="src/app/homepage/components/PackageShowcase.tsx" data-component-line="262" data-component-file="PackageShowcase.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22ml-2%22%7D" name="ArrowRightIcon" size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PackageShowcase;